class LocationService {
  constructor() {
    this.watchId = null;
    this.toRad = deg => deg * Math.PI / 180;
  }

  // Check if localStorage is available
  safeGetItem(key) {
    try {
      return localStorage?.getItem?.(key);
    } catch (e) {
      console.warn('localStorage.getItem failed:', e);
      return null;
    }
  }

  safeSetItem(key, value) {
    try {
      localStorage?.setItem?.(key, value);
    } catch (e) {
      console.warn('localStorage.setItem failed:', e);
    }
  }

  // Get user's current location
  /**
   * Returns the current coordinates or throws after a timeout.
   * Some Android WebViews never call error callback when system-wide
   * location is OFF, so we enforce a timeout (default 10 s) to avoid
   * hanging forever.
   */
  getCurrentLocation(timeoutMs = 10000) {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('GEO_NOT_SUPPORTED'));
        return;
      }

      const timer = setTimeout(() => {
        reject(new Error('GEO_TIMEOUT'));
      }, timeoutMs);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timer);
          resolve(position.coords);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  // Get full address from coordinates using Google Geocoding API
  async getLocationName(latitude, longitude) {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`
      );
      const data = await response.json();

      if (data.results && data.results.length > 0) {
        const addressComponents = data.results[0].address_components;
        let locationInfo = {
          district: '',
          state: '',
          country: '',
          fullAddress: data.results[0].formatted_address
        };

        addressComponents.forEach(component => {
          const types = component.types;
          if (types.includes('locality') || types.includes('sublocality')) {
            locationInfo.district = component.long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            locationInfo.state = component.long_name;
          }
          if (types.includes('country')) {
            locationInfo.country = component.long_name;
          }
        });

        return locationInfo;
      }
      return null;
    } catch (error) {
      console.error('Error getting address:', error);
      return null;
    }
  }

  haversineDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Earth radius in km
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(this.toRad(lat1)) *
      Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  startLocationWatch(successCallback) {
    if (!navigator.geolocation) {
      console.log('Geolocation is not supported by your browser');
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0
    };

    try {
      this.watchId = navigator.geolocation.watchPosition(
        successCallback,
        null,
        options
      );
    } catch (err) {
      console.error('Error starting location watch:', err);
    }
  }

  stopLocationWatch() {
    if (this.watchId) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }
  }

  async updateUserLocation(coords = null) {
    try {
      if (!coords) {
        coords = await this.getCurrentLocation();
      }
    } catch (err) {
      console.warn('LocationService: failed to get coordinates:', err?.message || err);
      return null; // Graceful fallback
    }

    const currentLat = coords.latitude;
    const currentLng = coords.longitude;

    const storedLat = this.safeGetItem('userLatitude');
    const storedLng = this.safeGetItem('userLongitude');

    let currentLocation;

    if (storedLat && storedLng) {
      const distance = this.haversineDistance(
        currentLat,
        currentLng,
        parseFloat(storedLat),
        parseFloat(storedLng)
      );
      if (distance > 1) {
        currentLocation = await this.getLocationName(currentLat, currentLng);
        this.safeSetItem('userLatitude', currentLat);
        this.safeSetItem('userLongitude', currentLng);
        this.safeSetItem('userLocation', JSON.stringify(currentLocation));
      } else {
        currentLocation = JSON.parse(this.safeGetItem('userLocation'));
      }
    } else {
      currentLocation = await this.getLocationName(currentLat, currentLng);
      this.safeSetItem('userLatitude', currentLat);
      this.safeSetItem('userLongitude', currentLng);
      this.safeSetItem('userLocation', JSON.stringify(currentLocation));
    }

    return currentLocation;
  }
}

export default new LocationService();
